/**
 * useAgreementSealing — the automatic finalize, and the latch that makes it fire once.
 *
 * COUNTERSIGN-LAST IS WHAT MAKES THIS WORK WITHOUT A WORKER. The admin who takes the last
 * signature is on the page, so their session renders and stores the executed PDF. The
 * ten-second poll retries it for a tab that was closed mid-flight, and "Retry seal" covers
 * the straggler nobody came back for.
 *
 * KEYED OFF THE DOCUMENTS, NOT OFF THE BOARD'S SLOTS. The board used to walk `slots` and take
 * `slot.document`; every one of those documents comes from `buildBoardSlots`' own
 * `input.documents`, so the document list is a superset of what the slot walk saw — and the
 * only members of the difference are COMPLETED documents that no slot could claim (a deposit
 * agreement whose arc row the subjects read did not return, for instance). Those are exactly
 * the ones that used to be sealed by nobody. The live-status filter `buildBoardSlots` applies
 * cannot change the answer either: COMPLETED is itself a live status, so it never removed a
 * document this predicate would have matched.
 *
 * ONE LATCH PER MOUNT TREE, VIA CONTEXT. Finalization is idempotent server-side but it is not
 * free, and two independent latches means the second caller re-fires a call the first already
 * had out — arriving at AGREEMENT_FINALIZE_IN_PROGRESS, which its row then holds as a failure
 * needing a human. `AgreementSealingProvider` puts one instance above every surface that shows
 * the same documents; `useAgreementSealingWithFallback` gives a surface mounted on its own
 * (the deposits page) a private instance, so nothing there changes.
 *
 * THE LATCH IS SHARED, SO THE REFRESHES HAVE TO FAN OUT. A consumer that joins the shared
 * instance stops making calls of its own, and the seal that changes the row it is showing is
 * then one it never fired — its `onRefresh` would never run, and whatever caches it invalidates
 * would keep serving the pre-seal row. So the sealing instance keeps a registry: consumers
 * register their `onRefresh` for as long as they are mounted (`useEffect`, unregistered on
 * unmount) and a seal that succeeds runs every registered callback alongside the instance's own.
 * Registration goes through a ref, so a consumer whose `onRefresh` is a fresh closure on every
 * render does not churn the registry.
 */

import {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { finalizeAgreement, type AgreementDocumentRow } from '@/lib/agreements/api';
import { parseAgreementError } from '@/lib/agreements/constants';
import type { SealAttempt } from './boardSlots';
import { reportAgreementError } from './useContractAgreements';

export interface AgreementSealing {
    /**
     * What THIS SESSION knows about the finalize call for one document — null when no call
     * has been made, or when the last one succeeded. `finalized_at` is the server's only
     * record, so a request in flight and one that came back refusing live nowhere but here.
     */
    attemptFor: (documentId: number | null | undefined) => SealAttempt | null;
    /**
     * IT CLEARS THE LATCH AS WELL AS THE FAILURE. The automatic attempt is deliberately
     * one-per-document-per-session, so leaving the latch set would make the failed attempt
     * the last one this tab could ever make.
     */
    retrySeal: (documentId: number) => void;
    /**
     * SUBSCRIBE TO THE SEALS THIS INSTANCE MAKES. A surface sharing the instance fires no
     * finalize of its own, so this is the only thing that can tell it the row it displays has
     * gained its `finalized_at`. Returns the unregister — call it on unmount.
     */
    registerRefresh: (onRefresh: () => void) => () => void;
}

export interface UseAgreementSealingOptions {
    /** The documents to seal. Pass null/undefined to make the instance inert. */
    documents: AgreementDocumentRow[] | null | undefined;
    /** Re-read whatever displayed the pre-seal state, after a finalize call settles. */
    onRefresh?: () => void;
}

export function useAgreementSealing({
    documents,
    onRefresh,
}: UseAgreementSealingOptions): AgreementSealing {
    const [attempts, setAttempts] = useState<Record<number, SealAttempt>>({});

    /**
     * Documents this session has already asked the edge function to finalize. Finalization is
     * idempotent server-side, but the ten-second poll would otherwise re-fire it on every tick
     * while the render is still running.
     */
    const finalizeAttempted = useRef<Set<number>>(new Set());

    /**
     * The `onRefresh` of every surface sharing this instance. Held in a ref rather than state
     * because registering must not re-render the instance — it is the sealing latch, and a
     * consumer mounting under it is no reason to re-run anything here.
     */
    const refreshSubscribers = useRef<Set<() => void>>(new Set());

    const registerRefresh = useCallback((subscriber: () => void) => {
        refreshSubscribers.current.add(subscriber);
        return () => {
            refreshSubscribers.current.delete(subscriber);
        };
    }, []);

    const clearAttempt = useCallback((documentId: number) => {
        setAttempts((current) => {
            if (!(documentId in current)) return current;
            const next = { ...current };
            delete next[documentId];
            return next;
        });
    }, []);

    const finalize = useMutation({
        mutationFn: (documentId: number) => finalizeAgreement(documentId),
        onMutate: (documentId) => {
            // The latch is set HERE rather than at the call sites, so that the automatic
            // attempt and a manual retry both count as the one attempt this session made.
            finalizeAttempted.current.add(documentId);
            setAttempts((current) => ({ ...current, [documentId]: { status: 'running' } }));
        },
        onSuccess: (result, documentId) => {
            clearAttempt(documentId);
            if (!result.already_finalized) toast.success('Executed PDF stored');
            onRefresh?.();
            // A copy, so a subscriber that unregisters while being notified cannot mutate the
            // set mid-iteration. Failure notifies nobody on purpose: nothing changed server-side
            // for a caller to re-read, and the state a shared consumer needs — the failed
            // attempt — reaches it through the context value, not through its own cache.
            for (const subscriber of [...refreshSubscribers.current]) subscriber();
        },
        onError: (error, documentId) => {
            // THE ROW KEEPS THE REASON. A toast is gone in seconds and the automatic attempt
            // fires once per document per session, so a failure that lived only in the toast
            // left the row spinning over a request nobody was making. The latch deliberately
            // stays set: re-firing on the next poll tick would retry a font upload nobody
            // has done yet. "Retry seal" is what clears both.
            const parsed = parseAgreementError(error);
            setAttempts((current) => ({
                ...current,
                [documentId]: { status: 'failed', message: parsed.message, code: parsed.code },
            }));
            reportAgreementError(error);
        },
    });

    useEffect(() => {
        for (const doc of documents ?? []) {
            if (doc.status !== 'COMPLETED' || doc.finalized_at) continue;
            if (finalizeAttempted.current.has(doc.id)) continue;
            // A running call has its own latch already; a failed one is waiting on a human.
            if (attempts[doc.id]) continue;
            finalize.mutate(doc.id);
        }
        // `finalize` is recreated on every mutation state change; depending on it here would
        // loop. The ref above is the guard that makes one attempt per document per session.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [documents]);

    const attemptFor = useCallback(
        (documentId: number | null | undefined): SealAttempt | null =>
            documentId == null ? null : attempts[documentId] ?? null,
        [attempts],
    );

    const retrySeal = useCallback(
        (documentId: number) => {
            finalizeAttempted.current.delete(documentId);
            clearAttempt(documentId);
            finalize.mutate(documentId);
        },
        [clearAttempt, finalize],
    );

    // AgreementsBoard hands this straight to the provider, and the board re-renders for plenty
    // of reasons that have nothing to do with sealing. A fresh object each time would re-render
    // every consumer of the context on every one of them.
    return useMemo(
        () => ({ attemptFor, retrySeal, registerRefresh }),
        [attemptFor, retrySeal, registerRefresh],
    );
}

// ─── Sharing one latch across the surfaces that show the same documents ──────

export const AgreementSealingContext = createContext<AgreementSealing | null>(null);

export const AgreementSealingProvider = AgreementSealingContext.Provider;

/**
 * The shared latch when one is above this component, a private one when it is alone.
 *
 * The local instance is fed no documents when a shared one exists, so it never fires: the two
 * cannot both be attempting the same seal. On the deposits page — where nothing provides the
 * context — the local instance is the only one, and that surface behaves exactly as it did
 * before this hook existed.
 *
 * WHICH IS WHY `onRefresh` IS REGISTERED, NOT JUST PASSED DOWN. Handing it to the local
 * instance is enough only while that instance is the one seals run through; under a provider
 * the local instance is inert, and a caller's refresh would go with it. Registering it with the
 * shared instance is what keeps the caller's cache invalidations running off a seal it did not
 * itself fire.
 */
export function useAgreementSealingWithFallback(
    options: UseAgreementSealingOptions,
): AgreementSealing {
    const shared = useContext(AgreementSealingContext);
    const local = useAgreementSealing({
        documents: shared ? null : options.documents,
        onRefresh: options.onRefresh,
    });

    // Registration is by stable wrapper over the latest callback, so a caller that rebuilds
    // `onRefresh` every render re-registers nothing — it just gets called with its newest one.
    const onRefreshRef = useRef(options.onRefresh);
    useEffect(() => {
        onRefreshRef.current = options.onRefresh;
    });

    const registerRefresh = shared?.registerRefresh;
    useEffect(() => {
        if (!registerRefresh) return;
        return registerRefresh(() => onRefreshRef.current?.());
    }, [registerRefresh]);

    return shared ?? local;
}
